import cssesc from 'cssesc';
import {Component} from 'panel';
import ResizeObserver from 'resize-observer-polyfill';

import {formatMetricNumber, getAlphaLabelFromIndex} from 'common/util';
import {truncateMiddle} from 'common/util/string';
import {Section} from 'common/components/items-menu/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import * as colors from 'common/util/colors';
import {TooltipBulletStyle, TooltipItemType, TooltipSection} from 'common/components/chart-tooltip/types';
import {CountType} from 'common/report/funnels/models/types';

import {Alignment, LinkTooltip, NodeMenuEvent, NodeTooltip, SankeyNodeType, SankeyStepType} from './models/types';
import SANKEY_CONFIG from './sankey-config.json';
import {template} from './template';
import css from './index.inline.styl';
import {SankeyStep} from './models/sankey-step';
import {SankeyNode, SankeyNodeAttributes} from './models/sankey-node';
import {SankeyBreakdown} from './models/sanky-breakdown';
import {SankeyFlow} from './models/sankey-flow';

const COMPONENT_NAME = `mp-sankey-chart`;

type Link = {
  class: {
    'is-drop-off': boolean;
    'is-in-top-path': boolean;
    'is-secondary': boolean;
  };
  color: string;
  attrs: {
    d: string;
  };
  props: {
    edge: TODO;
    breakdown: TODO;
  };
  on: {
    mouseleave: () => void;
    mousemove: (ev: MouseEvent) => void;
  };
};

interface SankeyState {
  links: Link[] | null;
  lastColor: number;
  segmentColors: Record<string, {primary: string; secondary: string}>;
  selectedNode: string | null;
  selectedBreakdown: string | null;
  linkTooltip: LinkTooltip | null;
  nodeTooltip: NodeTooltip | null;
  topPath: Set<any>;
  removeStep: SankeyStep;
  selfWidth: number;
  selfHeight: number;
}

export class SankeyChart extends Component<SankeyState> {
  private tableElement;
  private nodeHeightFactor;
  private _data: SankeyFlow;
  private sankeyElement;
  private scrollerElement;
  private tableContentResizeObserver: ResizeObserver;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      alignbreakdowns: `boolean`,
      alignment: {type: `json`, default: []},
      alwaysbreakdown: `boolean`,
      'anchor-label-overrides': {type: `json`, default: []},
      'can-decrease-cardinality': `boolean`,
      'count-type': {type: `string`, default: CountType.Unique},
      'disable-add-step': `boolean`,
      'disable-anchor-event-labels': `boolean`,
      'disable-click-node': `boolean`,
      'disable-lift': `boolean`,
      'event-rename': {type: `json`, default: []},
      events: {type: `json`, default: []},
      reverse: `boolean`,
      'scroll-offset': `number`,
      'tooltip-enabled': `boolean`,
    };
  }

  constructor() {
    super();

    this.onBreakdownNodeClicked = this.onBreakdownNodeClicked.bind(this);
    this.clearSelectedNode = this.clearSelectedNode.bind(this);
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        links: null,
        lastColor: 0,
        segmentColors: {},
        selectedNode: null,
        selectedBreakdown: null,
        linkTooltip: null,
        nodeTooltip: null,
        topPath: new Set(),
        removeStep: null,
        selfWidth: 0,
        selfHeight: 0,
      },

      helpers: {
        isEmptyFlow: () => {
          return !this.data.steps.length || this.data.steps.every((step) => !step.nodes.length);
        },
        getAlignmentIcon: (alignment: Alignment) => {
          return alignment === Alignment.LEFT ? `caret-left` : `caret-right`;
        },
        getToggledAlignment: (alignment: Alignment) => {
          return alignment === Alignment.LEFT ? Alignment.RIGHT : Alignment.LEFT;
        },
        getJumpIcon: (alignment: Alignment) => {
          return alignment === Alignment.LEFT ? `jump-right` : `jump-left`;
        },
        getLabelIcon: (node: SankeyNode) => {
          let icon = null;
          if (node.type !== SankeyNodeType.Anchor || node.isAnchorNode()) {
            if (node.isDropOff) {
              icon = `circle-decrease`;
            } else if (node.isPruned) {
              icon = `table`;
            } else if (node.isAnchorNode()) {
              const alignment = this.attr(`alignment`).length
                ? this.attr(`alignment`)[node.section]
                : this.helpers.getDefaultAlignment(node.section);
              const jumpIcon = this.helpers.getJumpIcon(alignment);
              icon = jumpIcon;
            } else if (node.type !== SankeyNodeType.Anchor) {
              if (node.isCustomEvent) {
                icon = `custom`;
              } else {
                icon = `event`;
              }
            }
          }
          return icon;
        },
        getDefaultAlignment: (section: number) => {
          return section === 0 ? Alignment.RIGHT : Alignment.LEFT;
        },
        getFlowAnchorStep: () => this.data.getFirstAnchorStep(),
        getNodeAttributes: (node: SankeyNode, step: SankeyStep) => this.getNodeAttributes(node, step),
        isInRemoveRange: (step) => this.isInRemoveRange(step),
        getBreakdownSections: (): Section[] => {
          const items = this.data.breakdowns.map((breakdown) => {
            const {colors, name} = this.getBreakdownAttributes(breakdown);
            return {color: colors.primary, label: name, isSelected: true};
          });
          return [{items}];
        },
        getBreakdownAttributes: (breakdown) => this.getBreakdownAttributes(breakdown),
        onBreakdownNodeClicked: (ev, node, breakdown) => this.onBreakdownNodeClicked(ev, node, breakdown),
        onRemoveStepHover: (ev) => this.onRemoveStepHover(ev),
        renameEvents: (name) => {
          const idx = parseInt(name, 10);
          if (isNaN(idx)) {
            return name;
          } else {
            return this.attr(`event-rename`)[idx] || name;
          }
        },
        getAnchorLabelOverride: (anchorIndex: number, fallbackLabel: string): string => {
          const override = this.attr(`anchor-label-overrides`) && this.attr(`anchor-label-overrides`)[anchorIndex];
          return override || this.helpers.renameEvents(fallbackLabel);
        },
        truncateLabel: (label, maxChar) => {
          return truncateMiddle(label, maxChar);
        },
        updateAlignment: ({section, alignment}) => {
          this.dispatchEvent(new CustomEvent(`updateAlignment`, {composed: true, detail: {section, alignment}}));
        },
        updateSteps: ({operation, type, anchor, absolute}) => {
          const {removeStep} = this.state;
          if (removeStep) {
            this.update({
              removeStep: null,
            });
          }
          this.dispatchEvent(
            new CustomEvent(`updateSteps`, {
              detail: {operation, type, anchor, absolute, scrollOffset: this.getScrollPosition()},
            }),
          );
        },
        getAbsoluteSteps: ({step, operation, value}) => {
          let absoluteSteps: number;
          const delta = operation === `remove` ? -value : value;
          if (step.isReverse) {
            absoluteSteps = step.anchorStepIndex - step.step + delta;
          } else {
            absoluteSteps = step.step - step.anchorStepIndex + delta;
          }
          return absoluteSteps;
        },
        getAddStepsSections: () => [
          {
            items: [
              {label: `Add 1 Step`, value: 1},
              {label: `Add 3 Steps`, value: 3},
              {label: `Add 5 Steps`, value: 5},
            ],
          },
        ],

        onBreakdownNodeMouseMove: (ev, node, breakdown) => {
          if (!node || !breakdown || !this.attr(`tooltip-enabled`)) {
            return;
          }

          this.update({
            topPath: this.data.calculateTopFlowPath(node),
            selectedNode: node.id,
            selectedBreakdown: breakdown.id,
            ...(this.data.breakdowns.length > 1 &&
              this.attr(`tooltip-enabled`) && {
                nodeTooltip: {
                  sections: this.getBreakdownTooltipSection(node, breakdown),
                  clientX: ev.clientX,
                  clientY: ev.clientY,
                },
              }),
          });

          ev.stopPropagation();
          this.calculateFlowLinks();
        },

        onBreakdownNodeMouseLeave: () => {
          if (this.attr(`tooltip-enabled`)) this.clearSelectedNode();
          this.update({nodeTooltip: null});
        },

        // calculate link shapes once all nodes are in place
        onTableInserted: (vnode) =>
          requestAnimationFrame(() => {
            this.tableElement = vnode.elm;
            this.scrollToOffset(this.attr(`scroll-offset`));
            this.calculateFlowLinks();
          }),

        onTablePatched: () => {
          // leverage cache to prevent infinite loop of updates, the calculation is needed to update the
          // drawn links between edges after they're placed, without this call the edges are incorrectly
          // positioned
          this.calculateFlowLinks({usePositionCache: true});
        },

        onTableContentInserted: (vnode) =>
          requestAnimationFrame(() => {
            this.tableContentResizeObserver?.disconnect();
            this.tableContentResizeObserver = new ResizeObserver((entries) => {
              const {width, height} = entries[0].contentRect;
              this.update({selfWidth: width, selfHeight: height});
              this.calculateNodeHeightFactor();
              requestAnimationFrame(() => this.calculateFlowLinks());
            });

            this.tableContentResizeObserver.observe(vnode.elm);
          }),

        onSankeyInsert: (vnode) => {
          this.sankeyElement = vnode.elm;
        },

        onScrollerInsert: (vnode) => {
          this.scrollerElement = vnode.elm;
        },

        getMousemoveElement: () => {
          return this.sankeyElement;
        },

        getScrollElement: () => {
          return this.scrollerElement;
        },

        getLiftIcon: (isNegative: boolean): string => {
          return isNegative ? `arrow-down` : `arrow-up`;
        },

        getLiftTooltipText: (lift: string, isNegative: boolean): string => {
          const liftText = isNegative ? `-${lift}% under` : `+${lift}% over`;
          return `Users in this path had a conversion rate ${liftText} the overall conversion rate`;
        },

        getNodeAnchorLetterLabel(nodeSection: number): string {
          const letterIndex = nodeSection >= 0 ? nodeSection : null;
          return letterIndex !== null ? getAlphaLabelFromIndex(nodeSection) : null;
        },

        getWeightTooltipText: (
          weight: string,
          stepIndex: number,
          nodeSection: number,
          stepType: SankeyStepType,
          anchorStepIndex: number,
          nodeLabel: string,
          isAnchorNode: boolean,
        ): string => {
          let tooltipText = ``;
          const letterLabelFrom = this.helpers.getNodeAnchorLetterLabel(nodeSection - 1);
          const letterLabelTo = this.helpers.getNodeAnchorLetterLabel(nodeSection);

          let subject;
          switch (this.attr(`count-type`)) {
            case CountType.General:
              subject = `total times`;
              break;
            case CountType.Session:
              subject = `sessions`;
              break;
            case CountType.Unique:
            default:
              subject = `users`;
              break;
          }

          if (isAnchorNode) {
            tooltipText = `${weight}% of ${subject} converted from ${letterLabelFrom} to ${letterLabelTo} in ${
              stepIndex - anchorStepIndex
            } ${stepIndex - anchorStepIndex <= 1 ? `step` : `steps`}`;
          } else {
            if (stepType === SankeyStepType.Forward) {
              tooltipText = `${weight}% of ${subject}  did ${nodeLabel} ${stepIndex - anchorStepIndex} ${
                stepIndex - anchorStepIndex <= 1 ? `step` : `steps`
              } after ${letterLabelFrom}`;
            } else if (stepType === SankeyStepType.Reverse) {
              tooltipText = `${weight}% of ${subject}  did ${nodeLabel} ${anchorStepIndex - stepIndex} ${
                anchorStepIndex - stepIndex <= 1 ? `step` : `steps`
              } before ${letterLabelTo}`;
            } else {
              tooltipText = `${weight}% of ${subject} did ${nodeLabel} at ${letterLabelTo}`;
            }
          }
          return tooltipText;
        },

        // scroll-by amount for scroll-control buttons
        getScrollByAmount: (): number => {
          return this.state.selfWidth;
        },
      },
    };
  }

  get data() {
    return this._data;
  }

  set data(data) {
    if (data.steps) {
      this._data = data;
      this.calculateNodeHeightFactor();
      this.update();
    }
  }

  attributeChangedCallback(name: string, oldValue: null | string, newValue: null | string): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === `tooltip-enabled` && !this.attr(name)) {
      this.update({linkTooltip: null, nodeTooltip: null});
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this.onDisconnected(() => {
      document.removeEventListener(`click`, this.clearSelectedNode);
      this.tableContentResizeObserver?.disconnect();
    });
  }

  onRemoveStepHover(step: SankeyStep) {
    const {removeStep} = this.state;

    if (!step) {
      return;
    }

    if (removeStep) {
      this.update({
        removeStep: null,
      });
    } else {
      this.update({
        removeStep: step,
      });
    }
  }

  /**
   * Toggle selection and calculates top path on node click
   */
  onBreakdownNodeClicked(clickEvent: MouseEvent, node: SankeyNode, breakdown: SankeyBreakdown) {
    clickEvent.preventDefault();
    breakdown.color = this.getBreakdownColors(breakdown);
    const event: NodeMenuEvent = new CustomEvent(`nodeClick`, {
      detail: {
        node,
        breakdown,
        pageX: clickEvent.pageX,
        pageY: clickEvent.pageY,
      },
    });

    this.update({
      topPath: this.data.calculateTopFlowPath(node),
      selectedNode: node.id,
      selectedBreakdown: breakdown.id,
    });

    clickEvent.stopPropagation();
    document.addEventListener(`click`, this.clearSelectedNode);
    this.calculateFlowLinks();

    this.dispatchEvent(event);
  }

  /**
   * Resets top path selection
   */
  clearSelectedNode() {
    document.removeEventListener(`click`, this.clearSelectedNode);

    this.update({
      selectedNode: null,
      selectedBreakdown: null,
      topPath: new Set(),
    });

    this.calculateFlowLinks();
  }

  isInRemoveRange(step) {
    const {removeStep} = this.state;
    let removeRange = false;
    if (!!removeStep && removeStep.isReverse) {
      removeRange =
        step.anchor === removeStep.anchor && step.section === removeStep.section && step.step <= removeStep.step;
    } else if (!!removeStep && removeStep.isForward) {
      removeRange =
        step.anchor === removeStep.anchor && step.section === removeStep.section && step.step >= removeStep.step;
    }
    return removeRange;
  }

  /**
   * Generates attributes for a given node shape and its label
   */
  getNodeAttributes(node: SankeyNode, step: SankeyStep): SankeyNodeAttributes {
    const {topPath, removeStep} = this.state;
    const heights = (node.breakdowns || [node.totalCount]).map((b) => Math.ceil(b.totalCount * this.nodeHeightFactor));
    const height = heights.reduce((a, b) => a + b, 0);
    const isInTopPath = node.edges.concat(node.incomingEdges).some((edge) => topPath.has(edge.edgeId));
    const nodeType = node.type.toLowerCase();
    const isInRemoveRange = !!removeStep && (removeStep.nodes.includes(node) || this.isInRemoveRange(step));
    const stepType = step.type;
    const anchorStepIndex = step.anchorStepIndex;
    const disableClick = !!this.attr(`disable-click-node`);

    return {
      heights,
      height,
      isInTopPath,
      isInRemoveRange,
      nodeType,
      stepType,
      anchorStepIndex,
      disableClick,
    };
  }

  getBreakdownAttributes(breakdownEl) {
    const name = breakdownEl.segments.map((s) => s.values.join(` & `)).join(`/`);
    const colors = this.getBreakdownColors(breakdownEl);
    const height = Math.ceil(breakdownEl.totalCount * this.nodeHeightFactor);
    const flowsTotalCount = this.data.breakdowns.reduce((total, breakdown) => total + breakdown.totalCount, 0);

    return {
      flowsTotalCount,
      name,
      colors,
      height,
    };
  }

  getBreakdownColors(breakdown): {primary: string; secondary: string} {
    if (!Object.prototype.hasOwnProperty.call(this.state.segmentColors, breakdown.id)) {
      this.state.segmentColors[breakdown.id] = colors.getChartSegmentColorCombo(this.state.lastColor++);
    }

    return this.state.segmentColors[breakdown.id];
  }

  getBreakdownTooltipSection(node: SankeyNode, breakdown: SankeyBreakdown): TooltipSection[] {
    const percentage = formatMetricNumber((breakdown.totalCount / node.totalCount) * 100);
    return [
      {
        bulletColor: this.getBreakdownColors(breakdown).primary,
        bulletStyle: TooltipBulletStyle.Solid,
        rows: [
          {
            items: [
              {
                text: `${percentage}%`,
                type: TooltipItemType.Data,
              },
              {
                text: this.getBreakdownAttributes(breakdown).name,
                type: TooltipItemType.Text,
              },
            ],
          },
        ],
      },
    ];
  }

  calculateNodeHeightFactor() {
    // calculates the height/step weight ratio of the most spacious step
    // this allows to fit the column with most nodes inside the boundaries
    // of the chart and use that ratio as the global scale for the chart
    const stepHeights = this.data.steps.map((step) => {
      const numMappedSegments = step.nodes.reduce((count, node) => count + node.segments.length, 0);
      const numBreakdowns = step.nodes.reduce((count, node) => count + node.breakdowns.length, 0);
      const numNodesWithLift = this.attr(`disable-lift`) ? 0 : step.nodes.filter((node) => node.hasLift()).length;

      // all labels show at least two lines, take account mapped segments for extra lines
      const sumLabelHeights =
        2 * step.nodes.length * SANKEY_CONFIG.SEGMENT_VALUE_LINE_HEIGHT +
        numMappedSegments * SANKEY_CONFIG.SEGMENT_VALUE_LINE_HEIGHT +
        numNodesWithLift * SANKEY_CONFIG.SEGMENT_VALUE_LINE_HEIGHT +
        step.nodes.length * SANKEY_CONFIG.NODE_LABEL_PADDING_BOTTOM;
      const sumBreakdownHeights = numBreakdowns * (SANKEY_CONFIG.BREAKDOWN_MARGIN + SANKEY_CONFIG.BREAKDOWN_MIN_HEIGHT);
      const sumMarginHeights = step.nodes.length * SANKEY_CONFIG.PLOT_NODE_MARGIN_BOTTOM;
      const minRequiredHeight = sumLabelHeights + sumBreakdownHeights + sumMarginHeights;

      const contentHeight = this.state.selfHeight - SANKEY_CONFIG.HEADER_HEIGHT - 2 * SANKEY_CONFIG.COLUMN_PADDING;

      // add an additional 32px to the calculation so that sankey nodes can have varying scale
      return (Math.max(contentHeight, minRequiredHeight + 32) - minRequiredHeight) / step.weight;
    });
    this.nodeHeightFactor = Math.min(...stepHeights);
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  calculateFlowLinks({
    usePositionCache = false,
  }: {
    usePositionCache?: boolean;
  } = {}) {
    if (!this.tableElement) {
      return;
    }

    const links = [];
    const element = this.tableElement;
    const columnElement = `.sankey-table-column.is-step-column`;
    const columnWidth = element.querySelector(columnElement).offsetWidth;
    const fetchNodeElement = (id) => element.querySelector(`.sankey-node[data-id='${cssesc(id)}']`);

    // determines the horizontal offset for each beizer curve control points
    const beizerCurveDelta = columnWidth * SANKEY_CONFIG.CURVY_FACTOR;

    const {selectedNode} = this.state;

    // First initialize the from and to node offsets from the top of the node at which each edge starts.
    // Deltas are stored as objects because we want to pass by reference.
    const fromNodeTopDeltas = {};
    const toNodeTopDeltas = {};
    const fromNodeBreakdownTopDeltas = {};
    const toNodeBreakdownTopDeltas = {};
    for (const step of this.data.steps) {
      for (const node of step.nodes) {
        fromNodeTopDeltas[node.id] = {value: 0};
        toNodeTopDeltas[node.id] = {value: 0};

        const breakdownOffset = {value: 0};
        for (const breakdown of node.breakdowns) {
          if (!Object.prototype.hasOwnProperty.call(fromNodeBreakdownTopDeltas, node.id)) {
            fromNodeBreakdownTopDeltas[node.id] = {};
          }

          fromNodeBreakdownTopDeltas[node.id][breakdown.id] = {value: breakdownOffset.value};

          if (!Object.prototype.hasOwnProperty.call(toNodeBreakdownTopDeltas, node.id)) {
            toNodeBreakdownTopDeltas[node.id] = {};
          }

          toNodeBreakdownTopDeltas[node.id][breakdown.id] = {
            value: breakdownOffset.value,
          };

          breakdownOffset.value += breakdown.totalCount * this.nodeHeightFactor + SANKEY_CONFIG.BREAKDOWN_MARGIN;
        }
      }
    }

    // Calculate the starting and ending coordinates for the edges. Edges from the same breakdown are grouped
    // together so that they align with the cohort breakdown in the node.
    for (const step of this.data.steps) {
      for (const node of step.nodes) {
        const fromNode = fetchNodeElement(node.id);

        // Now for each edge withing a breakdown calculate its coordinates based on the offset found above,
        // Each offset is increased by the vertical size of the current edge in preparation for the next one.
        for (const edge of node.edges) {
          const toNode = fetchNodeElement(edge.id);
          let edgeCreated = false;
          for (const breakdown of edge.breakdowns) {
            const edgeHeight = breakdown.totalCount * this.nodeHeightFactor;
            let fromNodeTopDelta;
            let toNodeTopDelta;
            // In the case we want to show the breakdowns always or there is only one breakdown which is the default case
            // when not comparing cohorts both the from and to node have the edges aligned to their breakdowns always.
            if (this.attr(`alwaysbreakdown`) || this.data.breakdowns.length === 1) {
              fromNodeTopDelta = fromNodeBreakdownTopDeltas[node.id][breakdown.id];
              toNodeTopDelta = toNodeBreakdownTopDeltas[edge.id][breakdown.id];
              // If the from node is selected then the edges will only be aligned to the breakdowns in the from node.
            } else if (selectedNode && edge.id === selectedNode) {
              fromNodeTopDelta = fromNodeTopDeltas[node.id];
              toNodeTopDelta = toNodeBreakdownTopDeltas[edge.id][breakdown.id];
              // If the from node is selected then the edges will only be aligned to the breakdowns in the from node.
            } else if (selectedNode && node.id === selectedNode) {
              fromNodeTopDelta = fromNodeBreakdownTopDeltas[node.id][breakdown.id];
              toNodeTopDelta = toNodeTopDeltas[edge.id];
              // If this edge is not broken down stop.
            } else {
              break;
            }

            if (!this.attr(`alignbreakdowns`)) {
              // If we got here that means we want to breakdown this edge, but we don't want it to be aligned.
              fromNodeTopDelta = fromNodeTopDeltas[node.id];
              toNodeTopDelta = toNodeTopDeltas[edge.id];
            }

            const edgeShapeData = this.generateEdgeShapeData({
              beizerCurveDelta,
              edgeHeight,
              fromNode,
              fromNodeTopDelta: fromNodeTopDelta.value,
              toNode,
              toNodeTopDelta: toNodeTopDelta.value,
            });
            links.push(this.generateEdgeConfiguration(edge, breakdown, edgeShapeData));
            fromNodeTopDelta.value += edgeHeight;
            toNodeTopDelta.value += edgeHeight;
            edgeCreated = true;
          }
          // The edge is not broken down.
          if (!edgeCreated) {
            const edgeHeight = edge.totalCount * this.nodeHeightFactor;
            const edgeShapeData = this.generateEdgeShapeData({
              beizerCurveDelta,
              edgeHeight,
              fromNode,
              fromNodeTopDelta: fromNodeTopDeltas[node.id].value,
              toNode,
              toNodeTopDelta: toNodeTopDeltas[edge.id].value,
            });
            links.push(this.generateEdgeConfiguration(edge, null, edgeShapeData));
            fromNodeTopDeltas[node.id].value += edgeHeight;
            toNodeTopDeltas[edge.id].value += edgeHeight;
          }
        }
      }
    }

    // postpatch call leverages the positionCache to only update if the edges don't match the node position
    if (!this.state.links || !usePositionCache || !this.matchesCache(links)) {
      this.update({links});
      this.lastCachedValue = this.generateCacheValue(links);
    }
  }

  lastCachedValue = ``;
  generateCacheValue(links: Link[]) {
    return links.map((l) => l.attrs.d).join();
  }
  matchesCache(links: Link[]): boolean {
    const currentValue = this.generateCacheValue(links);
    return currentValue === this.lastCachedValue;
  }

  /**
   * Generates configuration for an edge by checking
   * if the given edge is currently highlighted
   */
  generateEdgeConfiguration(edge, breakdown, edgeShapeData): Link {
    const {topPath} = this.state;
    const color = breakdown ? this.getBreakdownColors(breakdown).primary : colors.gray3;

    return {
      class: {
        'is-secondary': edge.fromNode.isSecondaryPart || edge.isSecondaryPart,
        'is-drop-off': edge.fromNode.isDropOff || edge.isDropOff,
        'is-in-top-path': topPath.has(edge.edgeId),
      },
      color,
      props: {edge, breakdown},
      attrs: {d: edgeShapeData.join(` `)},
      on: {
        mousemove: (ev) => this.onLinkMouseMove(ev, edge, breakdown),
        mouseleave: () => this.update({linkTooltip: null}),
      },
    };
  }

  onLinkMouseMove(ev, edge, breakdown) {
    if (this.attr(`tooltip-enabled`)) {
      this.update({
        linkTooltip: {
          breakdown,
          edge,
          clientX: ev.clientX,
          clientY: ev.clientY,
        },
      });
    }
  }

  /**
   * Generates configuration for an edge to/from a breakdown legend node.
   */
  generateBreakdownEdgeConfiguration(breakdown, edgeShapeData) {
    return {
      class: {'is-breakdown': true},
      color: this.getBreakdownColors(breakdown).primary,
      attrs: {d: edgeShapeData.join(` `)},
    };
  }

  /**
   * Generates a curved SVG path data for an edge
   */
  generateEdgeShapeData({fromNode, toNode, edgeHeight, fromNodeTopDelta, toNodeTopDelta, beizerCurveDelta}) {
    const fromNodeMiddle = fromNode.offsetLeft + fromNode.offsetWidth / 2;
    const fromNodeRight = fromNodeMiddle + fromNode.offsetWidth / 2;
    const fromNodeTop = fromNode.offsetTop + fromNodeTopDelta;

    const toNodeMiddle = toNode.offsetLeft + toNode.offsetWidth / 2;
    const toNodeLeft = toNodeMiddle - toNode.offsetWidth / 2;
    const toNodeTop = toNode.offsetTop + toNodeTopDelta;

    const fromNodeBottom = fromNodeTop + edgeHeight;
    const toNodeBottom = toNodeTop + edgeHeight;

    return [
      `M`,
      fromNodeMiddle,
      fromNodeTop, // fromNode top middle
      `H`,
      fromNodeRight, // fromNode top right
      `C`,
      fromNodeRight + beizerCurveDelta,
      fromNodeTop,
      toNodeLeft - beizerCurveDelta,
      toNodeTop,
      toNodeLeft,
      toNodeTop, // top beizer curve
      `H`,
      toNodeMiddle, // toNode top middle
      `V`,
      toNodeBottom, // toNode bottom middle
      `H`,
      toNodeLeft, // toNode bottom left
      `C`,
      toNodeLeft - beizerCurveDelta,
      toNodeBottom,
      fromNodeRight + beizerCurveDelta,
      fromNodeBottom,
      fromNodeRight,
      fromNodeBottom, // bottom beizer curve
      `H`,
      fromNodeMiddle, // fromNode bottom middle
      `z`,
    ];
  }

  scrollToOffset(scrollOffset: number) {
    if (this.scrollerElement) {
      this.scrollerElement.scrollLeft = scrollOffset;
    }
  }

  getScrollPosition(): number {
    return this.scrollerElement?.scrollLeft ?? 0;
  }
}

export const SankeyChartEl = defineAndMakeJsxFactory(COMPONENT_NAME, SankeyChart);
export const SankeyChartReact = panel2react(COMPONENT_NAME, SankeyChart);
